import { Moment } from 'moment';
import { Port } from 'services/clients/ports';
import { Customer } from './Customer';
import { ShippingLine, Terminal } from './Shipping';

export const enum ImportStatus {
  Vessel = 'vessel',
  Yard = 'yard',
  Departed = 'departed',
  Rail = 'rail',
  Unknown = 'unknown',
}

export const enum HoldState {
  Unknown = 'unknown',
  Cleared = 'cleared',
  Hold = 'hold',
}

export const enum AppointmentStatus {
  Scheduled = 'scheduled',
  Completed = 'completed',
  Cancelled = 'cancelled',
  Other = 'other',
  Rescheduled = 'rescheduled',
}

export const enum LegacyContainerStatus {
  Terminated = 'terminated',
  GatedOut = 'gated_out',
  Import = 'import',
  OnVessel = 'on_vessel',
  NotFound = 'not_found',
  Loading = 'loading',
  Pending = 'pending',
}

export const enum CurrentContainerStatus {
  GatedOut = 'gated_out',
  Terminated = 'terminated',
  OnVessel = 'on_vessel',
  Manifested = 'manifested',
  AtTerminal = 'at_terminal',
  OnRail = 'on_rail',
  OffRail = 'off_rail',
  GateOutFromRailFacility = 'gate_out_from_rail_facility',
  Unknown = 'unknown',
}

interface Date {
  actual: string | null;
  estimated: string | null;
  timezone: string;
}

interface Location {
  port: Port | null;
  portRaw: string | null;
  terminal: Terminal | null;
  terminalRaw: string | null;
}

interface DetentionPeriod {
  totalNumberOfDays: number | null;
  numberOfDays: number | null;
  displayAlert: boolean;
  lastDetentionDate: string | null;
  estimatedPerDiemCharges: number | null;
  perDiemDailyRate: number | null;
}

interface PickupDates {
  originalLastFreeDay: string | null;
  lastFreeDay: string | null;
  originalGoodThroughDate: string | null;
  goodThroughDate: string | null;
  maximumPickupDate: string | null;
}

export interface Hold {
  raw: string | null;
  status: HoldState;
}

interface YardLocation {
  location: string | null;
  closedArea: boolean;
}

interface PickupInfoPickupAppointment {
  startTime: string | null;
  endTime: string | null;
  timezone: string;
}

interface DemurragePeriod {
  numberOfDays: number | null;
  displayAlert: boolean;
  demurrageDue: boolean;
  demurrageFeeUsCents: number | null;
}

export interface PickupInfo {
  terminalAppointmentSystemLink: string;
  terminalKey: string;
  terminalName: string;
  terminalPier: null | string;
  portName: null | string;
  portCode: null | string;
  portCollectionCode: null | string;
  timezone: string;
  isAvailableForPickup: boolean;
  pickupDates: PickupDates | null;
  customsHold: Hold;
  freightHold: Hold;
  lineHold: Hold;
  tmfHold: Hold;
  ctfHold: Hold;
  terminalHold: Hold;
  otherHold: Hold;
  yardLocation: YardLocation;
  demurragePeriod: DemurragePeriod;
  // This might not be needed since we have another endpoints for appointments
  pickupAppointment: PickupInfoPickupAppointment;
}

export interface ContainerEvent {
  chassis: null | string;
  drayageScac: null | string;
  occurred: string;
  portCode: null | string;
  portName: null | string;
  portCollectionCode: null | string;
  terminalKey: null | string;
  terminalName: null | string;
  terminalPier: null | string;
  timezone: string;
  vesselName: null | string;
}

export interface Shipment {
  fullBillOfLadingNumber: string;
  billOfLadingNumber: string;
  shippingLine: ShippingLine;
  finalDestinationRaw: string | null;
  departureDate: Date | null;
}

interface LatestTransactionPerDiemCalculation {
  transactionId: string;
  contractId: string;
  dayCount: number;
  disputablePercentage: number;
  estLfd: string;
  estTotalChargesCents: number;
  estSavingsCents: number;
}

export interface Container {
  companyName: string;
  containerNumber: string;
  containerType: string;
  containerTypeKey: string;
  customer: null | Customer;
  discharged: null | ContainerEvent;
  gatedIn: null | ContainerEvent;
  gatedOut: null | ContainerEvent;
  lastRefreshed: string;
  pickupInfo: null | PickupInfo;
  shipment: Shipment | null;
  shippingLine: string;
  shippingLineKey: string;
  status: LegacyContainerStatus;
  currentStatus: CurrentContainerStatus;
  transaction: string;
  transactionCreatedAt: string;
  motherVessel: null | string;
  vesselArrival: null | Date;
  pickupLocation: null | Location;
  returnLocation: null | Location;
  loadingLocation: null | Location;
  detentionPeriod: DetentionPeriod;
  latestPerDiemCalculation: LatestTransactionPerDiemCalculation;
  deletedAt: null | string;
  deletedBy: null | string;
}

interface BookedAppointment {
  companyScac: string;
  latestDetails: null | {
    appointmentCreatedDate: null | string;
    appointmentEndDate: string;
    appointmentInActual: null | unknown;
    appointmentOutActual: null | unknown;
    appointmentStartDate: string;
    appointmentStatus: string;
    appointmentStatusRaw: null | string;
    containerNumber: string;
    isImportBooked: boolean;
    shippingLine: null | unknown;
  };
  tasAppointmentId: string;
  terminalId: string;
  terminalName: string;
  timezone: string;
}

interface PickupAppointmentSlot {
  slotStart: string;
  slotEnd: string;
  appointmentsAvailable: unknown[];
  shiftId: string;
}

export interface PickupAppointment {
  appointmentsBookable: boolean;
  availableSlots: PickupAppointmentSlot[];
  company: string;
  companyScac: string;
  observed: string;
  scraperError: null | boolean;
  slotsAvailable: number;
  terminal: string;
  timezone: string;
}

export interface Appointment {
  pickupBookedAppointment: null | BookedAppointment;
  availablePickupAppointment: null | PickupAppointment;
  returnBookedAppointment: null | BookedAppointment;
  containerNumber: string;
  lastRefreshedPickupAppointments: string;
  lastTriggeredPickupAppointments: string;
  pickupAppointmentsTriggered: unknown;
}

export interface PerDiemReportStatus {
  containerNumber: string;
  generating: boolean;
}

export interface TerminalAppointment {
  availabilityId: null | string;
  chassisRestrictions: string[];
  periodFromDate: Moment;
  periodToDate: Moment;
  slots: unknown[];
  terminalName: string;
  terminalAppointmentUrl: string;
}

export type ReturnAppointmentsDictionary = Record<string, TerminalAppointment[]>;

export interface TriggerPickupAppointmentsResults {
  gate_transactions: boolean;
  pickup_appointments: boolean;
  pikcups: boolean;
}

export interface TriggerPickupAppointmentsDictResults {
  [key: string]: TriggerPickupAppointmentsResults;
}

export interface TriggerPickupAppointment {
  containerNumber: string;
  gateTransactions: boolean;
  pickupAppointments: boolean;
  pickups: boolean;
}
export interface TriggerPickupAppointmentDict {
  [key: string]: TriggerPickupAppointment;
}

export interface ShiftStatus {
  availabilityId: 'yes' | 'no' | null;
  containerTypeId: string;
  date: string;
  gateStatus: 'open' | 'close' | null;
  instructions: string;
  shiftId: 'shift1' | 'shift2';
  shippingLineId: string;
  terminalAppointmentUrl: string;
  terminalId: string;
  terminalName: string;
}
